import { unsafeCSS, html } from 'lit';
import { property, queryAll } from 'lit/decorators.js';
import { PopBaseComponent } from '../../../base/pop-base-component';
import { type ProductCenterExhibit } from './performance-tiles.model';
import { Constants } from './performance-tiles.constants';
import {
  FundReturnKey,
  Product,
  SortDirection,
  SortField,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model';
import { zustandStateToComponent } from '../../store/product-center.store';
import type {
  IGroupedFunds,
  ISelectedProductFromGroup,
} from '../../store/product-center.store.model';

import style from './performance-tiles.scss?inline';

export class PopPerformanceTilesComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  @queryAll('.tile-container') tileContainers!: NodeListOf<HTMLElement>;

  static readonly tagName = Constants.TAG_NAME;

  @property()
  filteredProducts: Product[] = [];

  @property()
  sortBy: SortField = SortField.FUND_NAME;

  @property()
  sortDirection: SortDirection = SortDirection.ASC;

  @property()
  public productsGroup: IGroupedFunds[] = [];

  @property()
  public selectedProductFromGroup: ISelectedProductFromGroup[] = [];

  @property()
  fundReturnKey: FundReturnKey = FundReturnKey.SINCE_INCEPTION;

  /*
   * Component Methods
   */

  update(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('componentObject')) {
      this.filteredProducts =
        (this.componentObject as ProductCenterExhibit)?.products ?? [];
      this.sortBy = SortField.FUND_NAME;
      this.sortDirection = SortDirection.ASC;
      this.productsGroup = [];
      this.selectedProductFromGroup = [];
    }
    super.update(changedProperties);
    this.makeTilesVisible();
  }

  firstUpdated() {
    this.makeTilesVisible();
  }

  makeTilesVisible() {
    this.tileContainers.forEach((tile) => {
      requestAnimationFrame(() => {
        tile.classList.add(Constants.VISIBLE_CLASS);
      });
    });
  }

  private compareNumbers(a: number, b: number): number {
    return (a - b) * (this.sortDirection === SortDirection.ASC ? 1 : -1);
  }

  private compareStrings(a: string, b: string): number {
    return (
      a.localeCompare(b) * (this.sortDirection === SortDirection.ASC ? 1 : -1)
    );
  }

  private compareValues(a: string | number, b: string | number): number {
    if (typeof a === 'number' && typeof b === 'number') {
      return this.compareNumbers(a, b);
    } else if (typeof a === 'string' && typeof b === 'string') {
      return this.compareStrings(a, b);
    }
    return 0;
  }

  private sortPerformanceTileData() {
    if (!this.filteredProducts) {
      return;
    }

    const sortedData: ProductCenterExhibit = {
      products: [...this.filteredProducts],
    };

    sortedData.products.sort((a: Product, b: Product) => {
      const fieldValueA = a.productName[this.sortBy];
      const fieldValueB = b.productName[this.sortBy];
      return this.compareValues(fieldValueA, fieldValueB);
    });

    this.filteredProducts = [...sortedData.products];
  }

  renderComponent() {
    if (this.filteredProducts.length > 0) {
      this.sortPerformanceTileData();

      const isProductInGroup = (product: Product) => {
        return (
          product.parentProductCode &&
          this.productsGroup.some(
            (group) => group.parentProductCode === product.parentProductCode,
          ) &&
          this.selectedProductFromGroup.some(
            (item) =>
              item.parentProductCode === product.parentProductCode &&
              item.productCode !== product.productCode,
          )
        );
      };

      const isProductChartEmpty = (product: Product) => {
        return (
          product &&
          product.cumulativeChart &&
          product.cumulativeChart.data &&
          ((product.cumulativeChart.data[this.fundReturnKey] &&
            product.cumulativeChart?.data?.[this.fundReturnKey]?.length ===
              0) ||
            !product.cumulativeChart.data[this.fundReturnKey])
        );
      };
      const isComponentObjectActive = !!this.componentObject;
      let filteredProducts = this.filteredProducts;

      if (!isComponentObjectActive) {
        filteredProducts = filteredProducts.map((product: Product): Product => {
          return {
            ...product,
            cumulativeChart: {
              benchmarks: product.cumulativeChart?.benchmarks,
              data: {
                [this.fundReturnKey]:
                  product.cumulativeChart?.data?.[this.fundReturnKey] ?? null,
              },
            },
          };
        });
      }

      return html`
        <div class="tiles-container">
          ${filteredProducts.map((product: Product) => {
            if (isProductInGroup(product) || isProductChartEmpty(product)) {
              return null;
            }
            return html`
              <div class="tile-container">
                <pop-pc-tile-component
                  defer-until="visible"
                  data-component-object=${JSON.stringify({ product })}
                  .country="${this.country}"
                  .language="${this.language}"
                ></pop-pc-tile-component>
              </div>
            `;
          })}
        </div>
      `;
    } else {
      return html``;
    }
  }

  render() {
    if (!this.filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          fundReturnKey:
            productCenterState.performanceTileView.currentFundReturnKey,
          sortBy: productCenterState.performanceTileView.sortField,
          sortDirection: productCenterState.performanceTileView.sortDirection,
          productsGroup: productCenterState.performanceTileView.productsGroup,
          selectedProductFromGroup:
            productCenterState.performanceTileView.selectedProductFromGroup,
          filteredProducts: productCenterState.getFilteredProducts(),
        };
      },
    },
    PopPerformanceTilesComponent,
  ),
);
